<div class="flex items-start justify-between gap-4">
  <h2 mat-dialog-title>{{ 'app.review.export.history.title' | translate }}</h2>
  <button type="button"
          class="mr-4 mt-4 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
          [attr.aria-label]="'app.review.export.history.close' | translate"
          (click)="close()">
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>
  </button>
</div>

<mat-dialog-content class="w-full">
  @if (loading()) {
    <mat-progress-bar mode="indeterminate" data-testid="history-loading"></mat-progress-bar>
  } @else if (loadFailed()) {
    <p class="py-6 text-center text-sm text-error-600" data-testid="history-error">
      {{ 'app.review.export.history.loadFailed' | translate }}
    </p>
  } @else if (rows().length === 0) {
    <p class="py-6 text-center text-sm text-gray-500 dark:text-gray-400" data-testid="history-empty">
      {{ 'app.review.export.history.empty' | translate }}
    </p>
  } @else {
    <div class="w-full overflow-x-auto">
      <table mat-table [dataSource]="rows()" class="w-full" data-testid="history-table">
        <ng-container matColumnDef="createdAt">
          <th mat-header-cell *matHeaderCellDef class="!bg-blue-100">{{ 'app.review.export.history.columns.createdAt' | translate }}</th>
          <td mat-cell *matCellDef="let row" class="whitespace-nowrap">{{ row.export.createdAt | date:'short' }}</td>
        </ng-container>

        <ng-container matColumnDef="fileName">
          <th mat-header-cell *matHeaderCellDef class="!bg-gray-100">{{ 'app.review.export.history.columns.fileName' | translate }}</th>
          <td mat-cell *matCellDef="let row" class="break-all">{{ row.export.fileName }}</td>
        </ng-container>

        <ng-container matColumnDef="reviews">
          <th mat-header-cell *matHeaderCellDef class="!bg-blue-100">{{ 'app.review.export.history.columns.reviews' | translate }}</th>
          <td mat-cell *matCellDef="let row" class="tabular-nums">{{ row.export.reviewsWritten | number }}</td>
        </ng-container>

        <ng-container matColumnDef="expiresAt">
          <th mat-header-cell *matHeaderCellDef class="!bg-gray-100">{{ 'app.review.export.history.columns.availableUntil' | translate }}</th>
          <td mat-cell *matCellDef="let row" class="whitespace-nowrap">{{ row.export.expiresAt | date:'short' }}</td>
        </ng-container>

        <ng-container matColumnDef="info">
          <th mat-header-cell *matHeaderCellDef class="!bg-blue-100"><span class="sr-only">{{ 'app.review.export.history.filterInfo' | translate }}</span></th>
          <td mat-cell *matCellDef="let row">
            <button type="button"
                    class="flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                    cdkOverlayOrigin
                    #infoOrigin="cdkOverlayOrigin"
                    data-testid="filter-info"
                    [disabled]="!row.networkPills"
                    [attr.aria-label]="(row.networkPills ? 'app.review.export.history.filterInfo' : 'app.review.export.history.filterUnavailable') | translate"
                    [attr.aria-describedby]="infoOpenId() === row.export.id ? 'export-filter-' + row.export.id : null"
                    (mouseenter)="showInfo(row.export.id)"
                    (mouseleave)="hideInfo(row.export.id)"
                    (focus)="showInfo(row.export.id)"
                    (blur)="hideInfo(row.export.id)">
              i
            </button>

            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="infoOrigin"
              [cdkConnectedOverlayOpen]="!!row.networkPills && infoOpenId() === row.export.id"
              (detach)="hideInfo(row.export.id)">
              <div [id]="'export-filter-' + row.export.id"
                   role="tooltip"
                   class="mt-1 max-w-sm rounded-lg border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark"
                   data-testid="filter-popover">
                <app-filter-pills [periodPill]="row.periodPill" [networkPills]="row.networkPills" [expanded]="true" [wrap]="true" />
              </div>
            </ng-template>
          </td>
        </ng-container>

        <ng-container matColumnDef="download">
          <th mat-header-cell *matHeaderCellDef class="!bg-gray-100"><span class="sr-only">{{ 'app.review.export.history.download' | translate }}</span></th>
          <td mat-cell *matCellDef="let row">
            <button type="button"
                    class="flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
                    data-testid="history-download"
                    [disabled]="!!downloadingId()"
                    [attr.aria-label]="'app.review.export.history.download' | translate"
                    (click)="downloadExport(row)">
              @if (downloadingId() === row.export.id) {
                <svg class="animate-spin" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-opacity="0.25" stroke-width="2"/>
                  <path d="M17 10a7 7 0 0 0-7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              } @else {
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M10 3v9m0 0 3.5-3.5M10 12 6.5 8.5M4 13.5v1.75C4 16.22 4.78 17 5.75 17h8.5c.97 0 1.75-.78 1.75-1.75V13.5"
                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              }
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
      </table>
    </div>
  }
</mat-dialog-content>
